// frontend/src/features/livestock/OrganismResults.tsx
// The organisms matching the assign view's search (plan 4.4), as a listbox
// the search box drives, ending with "Add "{query}" as new". Rows, or a grid
// of large tiles on a computer (pickerThumbSize).

import { useState } from 'react';
import { Avatar, Box, Button, Group, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconContainer, IconPlus } from '@tabler/icons-react';
import type { Livestock } from '@/types/livestock';
import { LivestockPickerRow } from './LivestockPickerRow';
import type { TankShotPlace } from './organismSearch';
import type { PickerThumbSize } from './pickerThumbSize';
import classes from './AssignPhotoView.module.css';
import rowClasses from './LivestockPickerRow.module.css';

/** Rows drawn at first: a long list (300 organisms) redrawn on every key is slow on phones. */
export const RESULTS_SHOWN = 50;

/** "Tank shot" as the list's first option (tank shots design 6.1). */
export interface TankShotOption {
  /** Element id, for aria-activedescendant. */
  id: string;
  /** Before the organisms, or after them (organismSearch's tankShotPlace). */
  place: TankShotPlace;
  selected: boolean;
  /** The result Enter would pick. */
  active: boolean;
  onSelect: () => void;
}

const TANK_ROW_PICTURE: Record<Exclude<PickerThumbSize, 'large'>, number> = {
  small: 44,
  medium: 80,
};

/** The tank as an option, laid out as an organism's row, its picture a tank icon. */
function TankShotRow({
  option,
  size,
}: {
  option: TankShotOption;
  size: PickerThumbSize;
}): JSX.Element {
  const tile = size === 'large';
  const picture = (
    <Avatar
      size={tile ? 160 : TANK_ROW_PICTURE[size]}
      radius="sm"
      alt=""
      className={tile ? rowClasses.tilePicture : undefined}
    >
      <IconContainer size={20} />
    </Avatar>
  );
  const name = (
    <Text size="sm" fw={500} truncate lh={1.35} mt={tile ? 6 : undefined}>
      Tank shot
    </Text>
  );
  const detail = (
    <Text size="xs" c="gray.7" truncate lh={1.35}>
      The whole tank
    </Text>
  );
  return (
    <UnstyledButton
      id={option.id}
      role="option"
      tabIndex={-1}
      aria-selected={option.selected}
      data-name="Tank shot"
      data-selected={option.selected || undefined}
      data-active={option.active || undefined}
      data-size={size}
      className={rowClasses.row}
      onClick={option.onSelect}
    >
      {tile ? (
        <>
          <Box aria-hidden>{picture}</Box>
          {name}
          {detail}
        </>
      ) : (
        <Group gap="sm" wrap="nowrap">
          <Box aria-hidden style={{ flexShrink: 0 }}>
            {picture}
          </Box>
          <Box style={{ flex: 1, minWidth: 0 }}>
            {name}
            {detail}
          </Box>
        </Group>
      )}
    </UnstyledButton>
  );
}

export interface OrganismResultsProps {
  listId: string;
  /** Matches, best first. */
  results: Livestock[];
  /** The chosen organism. */
  selectedId: number | null;
  /** The result Enter would pick (phones). */
  activeId?: number | null;
  optionId: (organismId: number) => string;
  /** Stable (useCallback): rows are memoised. */
  onSelect: (organism: Livestock) => void;
  /** The search, trimmed; a new search shows the first 50 again. */
  query: string;
  /** What else narrows the list (the type filter), also starting over at 50. */
  filterKey: string;
  loading: boolean;
  /** The livestock list didn't load. */
  loadFailed: boolean;
  onRetryLoad: () => void;
  onAdd: () => void;
  /** Scroll inside (computer panel, phone search mode) rather than with the page. */
  scrolls?: boolean;
  /** The pictures' size: small or medium rows, or large tiles in a grid. */
  thumbSize?: PickerThumbSize;
  /** "Tank shot" first in the list; null or missing leaves it out. */
  tankOption?: TankShotOption | null;
}

export function OrganismResults({
  listId,
  results,
  selectedId,
  activeId = null,
  optionId,
  onSelect,
  query,
  filterKey,
  loading,
  loadFailed,
  onRetryLoad,
  onAdd,
  scrolls = false,
  thumbSize = 'small',
  tankOption = null,
}: OrganismResultsProps): JSX.Element {
  const resetKey = `${filterKey}|${query}`;
  const [showAllFor, setShowAllFor] = useState<string | null>(null);
  // The chosen one stays drawn when the arrow keys move past the first 50.
  const selectedIndex = results.findIndex((organism) => organism.id === selectedId);
  const limit =
    showAllFor === resetKey ? results.length : Math.max(RESULTS_SHOWN, selectedIndex + 1);
  const shown = results.length > limit ? results.slice(0, limit) : results;

  let empty: JSX.Element | null = null;
  if (results.length === 0) {
    if (loadFailed) {
      empty = (
        <Stack gap={6} align="center" py="sm">
          <Text size="sm" c="gray.7">
            The livestock list didn&apos;t load.
          </Text>
          <Button
            variant="light"
            size="compact-sm"
            h={36}
            onClick={onRetryLoad}
            aria-label="Retry loading livestock"
          >
            Retry
          </Button>
        </Stack>
      );
    } else if (!(query && tankOption)) {
      // (A search the tank shot answers has a match: no "Nothing matches".)
      empty = (
        <Text size="sm" c="gray.7" ta="center" py="sm">
          {loading
            ? 'Loading livestock…'
            : query
              ? `Nothing matches "${query}"`
              : 'No livestock in this tank yet'}
        </Text>
      );
    }
  }

  return (
    <Box className={classes.list} data-scrolls={scrolls || undefined}>
      <Stack
        gap={6}
        role="listbox"
        id={listId}
        aria-label={tankOption ? 'Tank shot and organisms' : 'Organisms'}
        className={thumbSize === 'large' ? classes.tiles : undefined}
      >
        {tankOption?.place === 'first' && <TankShotRow option={tankOption} size={thumbSize} />}
        {shown.map((organism) => (
          <LivestockPickerRow
            key={organism.id}
            id={optionId(organism.id)}
            livestock={organism}
            selected={organism.id === selectedId}
            active={organism.id === activeId}
            onSelect={onSelect}
            size={thumbSize}
          />
        ))}
        {tankOption?.place === 'after' && <TankShotRow option={tankOption} size={thumbSize} />}
      </Stack>
      {shown.length < results.length && (
        <Button variant="subtle" fullWidth h={44} mt={6} onClick={() => setShowAllFor(resetKey)}>
          {`Show all ${results.length}`}
        </Button>
      )}
      {empty}
      <UnstyledButton className={classes.addRow} onClick={onAdd}>
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon variant="light" size={44} radius="sm">
            <IconPlus size={16} />
          </ThemeIcon>
          <Text size="sm" fw={500} truncate>
            {query ? `Add "${query}" as new` : 'Add a new organism'}
          </Text>
        </Group>
      </UnstyledButton>
    </Box>
  );
}
